import { useEffect, useState } from 'react';
import { Loader2, Tag, Copy, ExternalLink, Search, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function MemberBenefits() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');

  useEffect(() => { api.get('/benefits/categories').then(({ data }) => setCats(data || [])).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (cat) qs.set('category', cat);
    if (q) qs.set('q', q);
    api.get(`/benefits${qs.toString() ? `?${qs}` : ''}`).then(({ data }) => setItems(data || [])).finally(() => setLoading(false));
  }, [cat, q]);

  const copy = async (code, id) => {
    try { await navigator.clipboard.writeText(code); setCopied(id); toast.success('Código copiado'); setTimeout(() => setCopied(''), 1800); }
    catch { toast.error('No se pudo copiar'); }
  };

  return (
    <div className="space-y-5" data-testid="member-benefits">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Beneficios exclusivos para socios</p>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">Descuentos y promociones CNIB</h1>
        <p className="mt-1 text-sm text-slate-600">Aprovecha convenios con partners del sector y marcas aliadas.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar beneficio" className="w-full rounded-full border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15" data-testid="member-benefits-search" />
        </div>
        <button onClick={() => setCat('')} className={`rounded-full px-3 py-1.5 text-xs font-bold ${!cat ? 'bg-cnib-navy text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}>Todas</button>
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${cat === c ? 'bg-orange-500 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}>{c}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-10 w-10 animate-spin text-cnib-blue" /></div> : items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-500" data-testid="member-benefits-empty">
          Aún no hay beneficios activos en esta categoría.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="member-benefits-grid">
          {items.map((b) => (
            <div key={b.benefit_id} className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl" data-testid={`member-benefit-${b.benefit_id}`}>
              <div className="relative flex items-center justify-between bg-gradient-to-br from-cnib-navy to-cnib-blue p-5">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-orange-300">{b.category}</p>
                  <p className="truncate font-heading text-base font-extrabold text-white">{b.partner_name}</p>
                </div>
                {b.partner_logo && <img src={b.partner_logo} alt="" className="h-12 w-12 shrink-0 rounded-xl bg-white object-contain p-1" />}
              </div>
              <div className="p-5">
                {b.featured && <span className="mb-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700"><Sparkles className="h-3 w-3" /> Destacado</span>}
                <p className="font-heading text-sm font-extrabold text-slate-900">{b.title}</p>
                <p className="mt-1 line-clamp-3 text-sm text-slate-600">{b.description}</p>
                {b.discount_text && (
                  <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-orange-500 px-3 py-1 text-xs font-bold text-white">
                    <Tag className="h-3 w-3" /> {b.discount_text}
                  </p>
                )}
                <div className="mt-4 space-y-2">
                  {b.code && (
                    <button onClick={() => copy(b.code, b.benefit_id)} className="flex w-full items-center justify-between rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-100" data-testid={`member-benefit-code-${b.benefit_id}`}>
                      <span>Código: <span className="font-mono text-cnib-blue">{b.code}</span></span>
                      {copied === b.benefit_id ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                    </button>
                  )}
                  {b.link_url && (
                    <a href={b.link_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 rounded-full bg-cnib-blue py-2 text-xs font-bold text-white hover:bg-cnib-blue/90" data-testid={`member-benefit-cta-${b.benefit_id}`}>
                      Ir al beneficio <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
